import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, Users, UserCheck, UserX, Search, UserPlus, KeyRound, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const { isMasterAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [resetDialog, setResetDialog] = useState({ userId: null, userName: '', newPassword: '', message: '', error: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    broker_type: 'senior',
    temp_password: '',
    role: 'user',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminRole = async (userId, currentRole) => {
    setUpdating(userId);
    try {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';
      await axios.put(`${API}/api/admin/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.response?.data?.detail || 'Error updating role');
    } finally {
      setUpdating(null);
    }
  };

  const setProcessorRole = async (userId, currentRole) => {
    setUpdating(userId);
    try {
      const newRole = currentRole === 'processor' ? 'user' : 'processor';
      await axios.put(`${API}/api/admin/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.response?.data?.detail || 'Error updating role');
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.temp_password.trim()) {
      setCreateError('All fields are required');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/api/admin/create-user`, newUser);
      setUsers([...users, response.data]);
      setCreateSuccess(`Account created for ${newUser.name}. Temporary password: ${newUser.temp_password}`);
      setNewUser({ name: '', email: '', broker_type: 'senior', temp_password: '', role: 'user' });
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Error creating user');
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!resetDialog.newPassword.trim() || resetDialog.newPassword.length < 4) {
      setResetDialog(prev => ({ ...prev, error: 'Password must be at least 4 characters' }));
      return;
    }
    try {
      await axios.put(`${API}/api/admin/users/${userId}/reset-password`, { new_password: resetDialog.newPassword });
      setResetDialog({ userId: null, userName: '', newPassword: '', message: `Password reset successfully`, error: '' });
    } catch (error) {
      setResetDialog(prev => ({ ...prev, error: error.response?.data?.detail || 'Error resetting password' }));
    }
  };

  const handleChangeBrokerType = async (userId, newType) => {
    try {
      await axios.put(`${API}/api/admin/users/${userId}/broker-type`, { broker_type: newType });
      setUsers(users.map(u => u.id === userId ? { ...u, broker_type: newType } : u));
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating broker type');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      setDeleteConfirm(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting user');
      setDeleteConfirm(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    if (role === 'master_admin') return <Badge className="bg-amber-500 text-black">Master Admin</Badge>;
    if (role === 'admin') return <Badge className="bg-blue-500 text-white">Admin</Badge>;
    if (role === 'processor') return <Badge className="bg-teal-500 text-white">Processor</Badge>;
    return <Badge variant="outline" className="text-gray-400 border-gray-600">User</Badge>;
  };

  const getBrokerTypeBadge = (type) => {
    const colors = {
      equity: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
      founding: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      executive: 'bg-green-500/20 text-green-400 border-green-500/50',
      senior: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
    };
    return (
      <Badge variant="outline" className={colors[type] || 'text-gray-400'}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    );
  };

  if (!isMasterAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="bg-[#111] border-red-900/50 max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-gray-400">Only the Master Admin can access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-amber-500" />
            Admin Panel
          </h1>
          <p className="text-gray-500 mt-1">Manage users, roles, and create accounts</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-sm text-gray-400">Total Users</p>
            <p className="text-2xl font-bold text-white">{users.length}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-400">Admins</p>
            <p className="text-2xl font-bold text-blue-400">
              {users.filter(u => u.role === 'admin' || u.role === 'master_admin').length}
            </p>
          </div>
        </div>
      </div>

      {/* Create New User */}
      <Card className="bg-[#111] border-[#222] border-l-4 border-l-green-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-500" />
            Create New Broker Account
          </CardTitle>
          <CardDescription className="text-gray-500">
            Create an account for a new broker with a temporary password. They can change it after logging in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateUser}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Full Name *</Label>
                <Input
                  placeholder="John Smith"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                  data-testid="create-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Email *</Label>
                <Input
                  type="email"
                  placeholder="john@kinfolkhomeloans.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                  data-testid="create-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Broker Type *</Label>
                <select
                  value={newUser.broker_type}
                  onChange={(e) => setNewUser({ ...newUser, broker_type: e.target.value })}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-white focus:border-green-500 focus:outline-none"
                  data-testid="create-user-broker-type"
                >
                  <option value="equity">Equity Partner</option>
                  <option value="founding">Founding</option>
                  <option value="executive">Executive</option>
                  <option value="processor">Processor</option>
                  <option value="senior">Senior</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Role *</Label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-white focus:border-green-500 focus:outline-none"
                  data-testid="create-user-role"
                >
                  <option value="user">Loan Officer</option>
                  <option value="processor">Processor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Temp Password *</Label>
                <Input
                  type="text"
                  placeholder="TempPass123"
                  value={newUser.temp_password}
                  onChange={(e) => setNewUser({ ...newUser, temp_password: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                  data-testid="create-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 opacity-0">Action</Label>
                <Button
                  type="submit"
                  disabled={creating}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  data-testid="create-user-submit"
                >
                  {creating ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Account
                    </>
                  )}
                </Button>
              </div>
            </div>
            {createError && (
              <p className="text-red-400 text-sm mt-3" data-testid="create-user-error">{createError}</p>
            )}
            {createSuccess && (
              <div className="mt-3 p-3 bg-green-900/20 border border-green-800 rounded-lg" data-testid="create-user-success">
                <p className="text-green-400 text-sm">{createSuccess}</p>
                <p className="text-green-300/60 text-xs mt-1">Share these credentials with the broker securely.</p>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="bg-[#111] border-[#222]">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1a1a1a] border-[#333] text-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card className="bg-[#111] border-[#222]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-green-500" />
            User Management
          </CardTitle>
          <CardDescription className="text-gray-500">
            Grant or revoke admin access to loan officers
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading users...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((user) => (
                <div 
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-[#0d0d0d] rounded-lg border border-[#222] hover:border-[#333] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-gray-500 text-sm">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <select
                      value={user.broker_type}
                      onChange={(e) => handleChangeBrokerType(user.id, e.target.value)}
                      className="bg-[#1a1a1a] border border-[#333] rounded-md px-2 py-1 text-sm text-white focus:border-green-500 focus:outline-none"
                      data-testid={`broker-type-select-${user.id}`}
                    >
                      <option value="equity">Equity Partner</option>
                      <option value="founding">Founding</option>
                      <option value="executive">Executive</option>
                      <option value="processor">Processor</option>
                      <option value="senior">Senior</option>
                    </select>
                    {getRoleBadge(user.role)}
                    
                    {user.role !== 'master_admin' && (
                      <div className="flex items-center gap-2">
                        {resetDialog.userId === user.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="text"
                              placeholder="New password"
                              value={resetDialog.newPassword}
                              onChange={(e) => setResetDialog(prev => ({ ...prev, newPassword: e.target.value, error: '' }))}
                              className="bg-[#1a1a1a] border-[#333] text-white h-8 w-36 text-sm"
                              data-testid={`reset-password-input-${user.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleResetPassword(user.id)}
                              className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                              data-testid={`reset-password-confirm-${user.id}`}
                            >
                              Set
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setResetDialog({ userId: null, userName: '', newPassword: '', message: '', error: '' })}
                              className="text-gray-400 h-8 text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setResetDialog({ userId: user.id, userName: user.name, newPassword: '', message: '', error: '' })}
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                            data-testid={`reset-password-btn-${user.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5 mr-1" />
                            Reset Password
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleAdminRole(user.id, user.role)}
                          disabled={updating === user.id || user.role === 'processor'}
                          className={
                            user.role === 'admin'
                              ? 'border-red-500/50 text-red-400 hover:bg-red-500/20'
                              : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/20'
                          }
                        >
                          {updating === user.id ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : user.role === 'admin' ? (
                            <>
                              <UserX className="w-4 h-4 mr-1" />
                              Remove Admin
                            </>
                          ) : (
                            <>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Make Admin
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setProcessorRole(user.id, user.role)}
                          disabled={updating === user.id || user.role === 'admin'}
                          className={
                            user.role === 'processor'
                              ? 'border-red-500/50 text-red-400 hover:bg-red-500/20'
                              : 'border-teal-500/50 text-teal-400 hover:bg-teal-500/20'
                          }
                          data-testid={`processor-role-btn-${user.id}`}
                        >
                          {user.role === 'processor' ? (
                            <>
                              <UserX className="w-4 h-4 mr-1" />
                              Remove Processor
                            </>
                          ) : (
                            <>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Make Processor
                            </>
                          )}
                        </Button>
                        {deleteConfirm === user.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                              data-testid={`delete-confirm-${user.id}`}
                            >
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirm(null)}
                              className="text-gray-400 h-8 text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteConfirm(user.id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            data-testid={`delete-user-btn-${user.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {filteredUsers.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No users found matching "{searchTerm}"</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-amber-900/20 border-amber-800/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <h3 className="text-amber-400 font-medium mb-1">Role Permissions</h3>
              <ul className="text-sm text-amber-200/70 space-y-1">
                <li>- <strong>Admins</strong> can view all loan officers' data, create/delete users, and manage loans</li>
                <li>- <strong>Processors</strong> can view all loans (company-wide) and edit them, but cannot add/delete loans or see commissions</li>
                <li>- <strong>Users</strong> (Loan Officers) can only see and manage their own loans</li>
                <li>- Only you (Master Admin) can grant or revoke admin/processor access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPanel;
